import {GoogleMapsOverlay} from '@deck.gl/google-maps';
import {GeoJsonLayer, IconLayer} from '@deck.gl/layers';
import {MVTLayer} from '@deck.gl/geo-layers';

import {MENU_BAR_WIDTH_PIXELS} from '@/constants/MainMenu';
import atlas from './markerAtlas.png';
import mappings from './markerMapping.js';
import {isMobile} from '@/lib/ReusableFunctions/User';

const HIGHLIGHT_COLOR = [255, 255, 255, 128]; // 50% white

/**
 * Wrapper for GoogleMapsOverlay from deck.gl, which is an implementation of
 * a Google Maps OverlayView
 * @param {import('@deck.gl/google-maps').GoogleMapsOverlayProps} data
 * @return {GoogleMapsOverlay}
 */
const createDeckglOverlay = (data) => {
  return new GoogleMapsOverlay({
    layers: data.layers,
    style: data.style,
    onError: (error) => {
      console.error('Error creating GoogleMapsOverlay', error);
    },
    getCursor: data.getCursorCallback ? data.getCursorCallback : () => null,
    interleaved: false, /* When set to true, re-uses the WebGL context from the Google map for better performance, when
    set to false, creates a new Deck.gl context. Raster maps cannot have interleaved overlays. */
    // On mobile, avoid using device pixels to improve performance by consuming less memory.
    useDevicePixels: !isMobile(),
  });
};

const c1 = 1.70158; // constant for the back easing function
const c3 = c1 + 1; // constant for the back easing function
/**
 * Transition for the position of the icon. This transition is an interpolation
 * that uses the [easeOutBack](https://easings.net/#easeOutBack) easing function.
 */
const positionTransition = {
  type: 'interpolation',
  duration: 400,
  easing: (x) => 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2), // https://easings.net/#easeOutBack
};

/**
 * Wrapper for the IconLayer from deck.gl. If a layer with the same id already exists
 * in the overlay, it will be replaced.
 * @param {import('@deck.gl/layers').IconLayerProps} options
 * @return {IconLayer}
 */
const createIconLayer = (options) => {
  /** @type {import('@deck.gl/layers').IconLayerProps} */
  const defaults = {
    getIcon: (d) => d.icon,
    getColor: (icon) => [0, 0, 0, !!icon.visible * 255], // 0 alpha if not visible
    getPosition: (icon) => {
      if (!icon.latLng) {
        console.error(`latLng is missing for icon: ${JSON.stringify(icon)}`);
        return [0, 0];
      }
      return [icon.latLng.lng, icon.latLng.lat];
    },
    onIconError: (event) => console.error('Error loading icons', event.error),
    // For documentation about iconAtlas and iconMapping, see
    // the confluence page titled "Pre-packing markers for deck.gl"
    iconAtlas: atlas,
    iconMapping: mappings,
    highlightColor: HIGHLIGHT_COLOR,
    alphaCutoff: 0.25,

    // Required to satisfy the type checker
    id: null,
    data: [],
  };
  const defaultTransitions = {
    getSize: {
      type: 'spring',
      stiffness: 0.4,
      damping: 0.2,
    },
  };
  Object.assign(defaultTransitions, options.transitions || {});
  Object.assign(defaults, options);
  defaults.transitions = defaultTransitions;
  return new IconLayer(defaults);
};

/**
 * Wrapper for MVTLayer from deck.gl. If a layer with the same id already exists in the overlay, it will be replaced.
 * @param {import('@deck.gl/geo-layers').MVTLayerProps} options
 * @return {MVTLayer}
 */
const createMVTLayer = (options) => {
  /** @type {import('@deck.gl/geo-layers').MVTLayerProps} */
  const defaults = {
    autoHighlight: true,
    binary: false, // needs to be false so `renderSubLayers` can filter features
    highlightColor: [255, 255, 255, 255],
    lineWidthUnits: 'pixels',
    pickable: true,
    pointRadiusUnits: 'pixels',
    renderSubLayers: (props) => {
      if (!props.data || !props.data.length) return null;
      return new GeoJsonLayer({
        ...props,
        data: props.data,
      });
    },
  };
  return new MVTLayer(Object.assign(defaults, options));
};

/**
 * Pick multiple objects from the overlay
 * @param {Object} options
 * @param {GoogleMapsOverlay} options.overlay
 * @param {number} options.x
 * @param {number} options.y
 * @param {number} options.radius
 * @param {number} options.depth
 * @param {string[]} options.layerIds
 * @return {import('@deck.gl/core').PickingInfo[] | null}
 */
const pickMultipleObjects = ({overlay, x, y, radius, depth, layerIds}) => {
  if (!x || !y) {
    return [];
  }
  const isSmallScreen = window.matchMedia('only screen and (max-width: 1020px)').matches;
  // Smaller screens do not need to account for the menu offset
  if (!isSmallScreen) {
    x = x - MENU_BAR_WIDTH_PIXELS;
  }
  return overlay.pickMultipleObjects({x, y, radius, depth, layerIds});
};

export {
  createDeckglOverlay,
  createIconLayer,
  createMVTLayer,
  pickMultipleObjects,
  positionTransition,
};
